import CustomPlacesAutocomplete from "../CustomPlacesAutocomplete";
import React, { useState, useCallback, useEffect } from "react";
import { DateTimeInput } from "../../components/layout/DateTimeInput";

export default function FormPickup({
  booking,
  setBookingField,
  isLoaded,
  handleChange,
  isNewBooking = false,
  onDriverReset,
}) {
  const [dateError, setDateError] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Initialize date value for existing bookings
  useEffect(() => {
    if (isLoaded && !initialized && booking.date) {
      if (!isNewBooking) {
        setDateValue(new Date(booking.date * 1000).toISOString().split("T")[0]);
      }
      setInitialized(true);
    }
  }, [isLoaded, booking.date, isNewBooking, initialized]);

  function setPickup(pickup) {
    setBookingField("pickup.address", pickup.display_name);
    setBookingField("pickup.full_address", pickup.display_name);
    // Use geocoding address if available (for API calls), otherwise use display_name
    setBookingField("pickup.geocoding_address", pickup.geocoding_address || pickup.display_name);
    
    // Store place_id for Google Places geocoding
    setBookingField("pickup.place_id", pickup.place_id || null);
    
    // Store coordinates directly if available (for predefined addresses)
    // OR reset them to null if not available (switching from predefined to Google address)
    setBookingField("pickup.lat", pickup.lat || null);
    setBookingField("pickup.lon", pickup.lon || null);
  }
  
  function handleDateChange(e) {
    const targetName = e.target.name;
    const targetValue = e.target.value;

    // Clear date error when date changes
    if (targetName === "date") {
      setDateError("");
      setDateValue(targetValue);

      // If date changes and a driver was selected, reset the driver
      if (booking.driver?.id) {
        setBookingField("driver", {
          id: "",
          name: "",
          phone: "",
          company: {
            name: "",
            id: "",
          },
        });
        if (onDriverReset) {
          onDriverReset();
        }
      }

      // Update the booking field immediately if valid
      const timestamp = new Date(targetValue).getTime();
      if (timestamp && !isNaN(timestamp)) {
        setBookingField("date", timestamp / 1000);
      }
    }
  }

  // Validate date format
  const validateDate = useCallback((dateString) => {
    if (!dateString) return "";

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      return "Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.";
    }

    // Validate that the date is actually valid (not 2025-02-30, etc.)
    const dateParts = dateString.split('-');
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    const dateObj = new Date(year, month - 1, day);

    if (
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day ||
      year < 1900 ||
      year > 2100
    ) {
      return "Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.";
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateObj.setHours(0, 0, 0, 0);

    if (dateObj < today) {
      return "La date de la course ne peut pas être dans le passé.";
    }

    return "";
  }, []);

  // Validate date when it loses focus
  const handleDateBlur = useCallback(() => {
    const error = validateDate(dateValue);
    setDateError(error);
  }, [dateValue, validateDate]);

  if (!isLoaded)
    return (
      <div className="kt-section kt-section--first">
        <p>Chargement...</p>
      </div>
    );

  return (
    <div className="kt-section kt-section--first">
      <div className="kt-section__body">
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Date du départ *</label>
          <div className="col-lg-6">
            <DateTimeInput
              type="date"
              value={dateValue}
              className={dateError ? "form-control is-invalid" : "form-control"}
              name="date"
              placeholder="JJ/MM/AAAA"
              onChange={handleDateChange}
              onBlur={handleDateBlur}
              disabled={booking.sharedBy}
            />
            {dateError && (
              <div className="invalid-feedback d-block">
                {dateError}
              </div>
            )}
          </div>
        </div>
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Heure du départ *</label>
          <div className="col-lg-6">
            <DateTimeInput
              type="time"
              value={booking.time || ""}
              className="form-control"
              name="time"
              onChange={handleChange}
              disabled={booking.sharedBy}
            />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Adresse de départ *</label>
          <div className="col-lg-6">
            <CustomPlacesAutocomplete
              setSelection={setPickup}
              value={booking.pickup?.address || ""}
              disabled={booking.sharedBy}
            />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">
            Adresse complète de départ:
          </label>
          <div className="col-lg-6">
            <input
              type="text"
              value={booking.pickup?.full_address || ""}
              className="form-control"
              name="pickup.full_address"
              onChange={handleChange}
              disabled={booking.sharedBy}
            />
          </div>
        </div>
        {/*<div className="form-group row">*/}
        {/*  <label className="col-lg-3 col-form-label">Step Address</label>*/}
        {/*  <div className="col-lg-6">*/}
        {/*    <CustomPlacesAutocomplete*/}
        {/*        setSelection={(obj) => setBookingField("step_address", obj.display_name)}*/}
        {/*        value={booking.step_address || ""}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    </div>
  );
}
